/**
 * How long to wait for the print file before giving up.
 *
 * The render itself takes two to three seconds, so the previous five-second
 * budget left almost no headroom: a second shopper, or the browser refetching
 * the same URL for the <img>, was enough to queue a render behind others and
 * blow it. The progress bar covers the wait, and failing early only shows an
 * error for work that was about to succeed.
 */
const DESIGN_IMAGE_TIMEOUT_MS = 15000;

/**
 * How many times to ask after the mockup, and how long between asks.
 *
 * A healthy Printful task completes in 15 to 50 seconds. A stalled one is
 * replaced by the backend after 45 seconds, and the replacement needs the same
 * again, so the budget covers an original plus its replacement with room to
 * spare. Twenty attempts at four seconds gave up before a replacement could
 * finish.
 */
const MOCKUP_POLL_ATTEMPTS = 45;
const MOCKUP_POLL_INTERVAL_MS = 4000;

/** How long the "your design is ready, the mug preview is not" notice stays up. */
const MOCKUP_NOTICE_MS = 7000;

class VariantPickerShipSailing extends HTMLElement {
  constructor() {
    super();
    this.shipSelect = this.querySelector("#ship-select");
    this.saildateSelect = this.querySelector("#saildate-select");
    this.formId = this.shipSelect.getAttribute("form");
    this.shipSelect.addEventListener(
      "change",
      this.handleShipChange.bind(this)
    );
    this.saildateSelect.addEventListener(
      "change",
      this.handleSaildateChange.bind(this)
    );
  }

  // Create a wrapper function for fetch with default configuration
  #fetchWithConfig(url, options = {}) {
    // Define your common configuration
    const defaultConfig = {
      headers: {
        "Content-Type": "application/json",
        "x-api-key": X_API_KEY,
      },
      credentials: "same-origin", // For cookies/auth
      mode: "cors",
    };

    // Merge the default config with any options passed to the function
    // This allows headers to be extended or overridden when needed
    const mergedOptions = {
      ...defaultConfig,
      ...options,
      headers: {
        ...defaultConfig.headers,
        ...(options.headers || {}),
      },
    };

    // Return the fetch call with the merged configuration
    return fetch(url, mergedOptions);
  }

  /**
   * Finds the gallery and modal images the picker writes into: the first
   * rendered slides take the Printful mockups, the slide after them the
   * generated design.
   *
   * Most products have one mockup. A two-sided product (a can cooler) asks for
   * one per side, front then back, so it needs one more slide. When a product
   * has too few images for every mockup it asked for, it shows as many as fit
   * rather than no preview at all.
   *
   * The order is read from the gallery as it stands right now, not from
   * anything rendered with the page. Dawn re-renders the gallery whenever the
   * shopper switches variant, putting that variant's featured image first and
   * hiding the other variants' images, so a list captured at page load named
   * the previous variant's image and every regeneration after a size change
   * failed with "media is not in the gallery". Dawn updates the gallery before
   * it publishes the variant change, so the gallery is current by the time
   * this runs.
   *
   * Slides are matched by id rather than position so a missing slide is
   * detected rather than silently shifting the indices, and the modal image
   * is looked up the same way.
   */
  #resolveMediaSlots(mockupsWanted = 1) {
    const order = [
      ...document.querySelectorAll(".product__media-list li[data-media-id]"),
    ].map((li) => li.dataset.mediaId.split("-").pop());

    if (order.length < 2) {
      throw new Error(
        `This product needs at least two images in the gallery — the first is replaced by the Printful mockup, the second by the generated design. It has ${order.length}.`
      );
    }

    const mockupCount = Math.min(mockupsWanted, order.length - 1);
    if (mockupCount < mockupsWanted) {
      console.warn(
        `[ship-saildate] this product asks for ${mockupsWanted} mockups but its gallery has ${order.length} images, so only ${mockupCount} will show. Add images so every mockup and the design have a slide.`
      );
    }

    const slot = (mediaId, label) => {
      const slide = document.querySelector(
        `.product__media-list li[data-media-id$="-${mediaId}"]`
      );
      const galleryImage = slide ? slide.querySelector("img") : null;
      const modalImage = document.querySelector(
        `.product-media-modal__content img[data-media-id="${mediaId}"]`
      );
      if (!galleryImage || !modalImage) {
        throw new Error(
          `The ${label} image (media ${mediaId}) is missing from the gallery or its modal.`
        );
      }
      return {
        show(url) {
          for (const img of [galleryImage, modalImage]) {
            img.src = url;
            // Every one of these describes the placeholder media, not what is
            // replacing it. width/height in particular carry the placeholder's
            // aspect ratio, which the browser uses to reserve the box.
            img.removeAttribute("srcset");
            img.removeAttribute("sizes");
            img.removeAttribute("width");
            img.removeAttribute("height");
          }
        },
      };
    };

    const slots = {
      mockups: order
        .slice(0, mockupCount)
        .map((mediaId, i) => slot(mediaId, `mockup ${i + 1}`)),
      design: slot(order[mockupCount], "design"),
    };

    // Anything past the mockups and the design is neither, so drop it. Dawn's
    // next variant re-render brings the full gallery back.
    const keep = new Set(order.slice(0, mockupCount + 1));
    document
      .querySelectorAll(".product__media-list li[data-media-id]")
      .forEach((li) => {
        if (!keep.has(li.dataset.mediaId.split("-").pop())) li.remove();
      });
    document
      .querySelectorAll(".product-media-modal__content img[data-media-id]")
      .forEach((img) => {
        if (!keep.has(img.dataset.mediaId)) img.remove();
      });

    return slots;
  }

  connectedCallback() {
    this.waitForForm().then(() => {
      this.fetchShips();
      this.printFileInput = this.productForm.querySelector("#printFileInput");
      this.summaryInput = this.productForm.querySelector("#summaryInput");
      this.addToCartButton = this.productForm.querySelector(
        "button.product-form__submit"
      );
      this.updateAddToCartButton();
      this.cartVariantChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.variantChange,
        (event) => this.onCartVariantChange(event)
      );
    });
  }

  disconnectedCallback() {
    if (this.cartVariantChangeUnsubscriber) {
      this.cartVariantChangeUnsubscriber();
    }
    this.#releaseDesignPreview();
  }

  async waitForForm() {
    return new Promise((resolve) => {
      const checkForm = () => {
        this.productForm = document.getElementById(this.formId);
        this.productForm ? resolve() : requestAnimationFrame(checkForm);
      };
      checkForm();
    });
  }

  async fetchCruiselines() {
    try {
      const response = await this.#fetchWithConfig(
        `${EXT_API_BASE_URL}/cruiselines`
      );
      if (!response.ok) {
        throw new Error("Failed to fetch cruiselines");
      }
      const { data: cruiselines, error } = await response.json();
      if (error) {
        throw new Error(error);
      }
    } catch (err) {
      console.error(err);
    }
  }

  async fetchShips() {
    try {
      const response = await this.#fetchWithConfig(`${EXT_API_BASE_URL}/ships`);
      if (!response.ok) {
        throw new Error("Failed to fetch ships");
      }
      const { data: ships, error } = await response.json();
      if (error) {
        throw new Error(error);
      }
      this.shipSelect.innerHTML = '<option value="">Select a ship</option>';
      ships.forEach((ship) => {
        const option = document.createElement("option");
        option.value = ship.slug;
        option.textContent = ship.name;
        this.shipSelect.appendChild(option);
      });
    } catch (err) {
      console.error(err);
    }
  }

  async fetchSaildates(shipId) {
    const shipSlug = this.shipSelect.value;
    try {
      const response = await this.#fetchWithConfig(
        `${EXT_API_BASE_URL}/itineraries/ship/${shipSlug}`
      );
      if (!response.ok) {
        throw new Error("Failed to fetch saildates");
      }
      const { data: saildates, error } = await response.json();
      if (error) {
        throw new Error(error);
      }

      this.saildateSelect.innerHTML =
        '<option value="">Select a saildate</option>';

      saildates.forEach((date) => {
        const option = document.createElement("option");
        option.value = date.id;
        option.textContent = date.pretty_saildate;
        this.saildateSelect.appendChild(option);
      });

      this.saildateSelect.disabled = false;
    } catch (err) {
      console.error(err);
    }
  }

  async handleShipChange(event) {
    const shipSlug = event.target.value;
    this.saildateSelect.value = "";
    this.saildateSelect.disabled = true;
    this.#clearCustomisation();

    if (shipSlug) {
      await this.fetchSaildates(shipSlug);
    }

    this.updateAddToCartButton();
  }

  async handleSaildateChange(event) {
    const itineraryId = event.target.value;
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
    await this.regenerateMockup(itineraryId);
    this.updateAddToCartButton();
  }

  async regenerateMockup(itineraryId) {
    this.#clearCustomisation();
    document.querySelector(
      ".mockup-regenerate-message .progress-bar"
    ).style.width = "100%";
    const selectedVariantEl =
      document.getElementById("selected-variant")?.textContent;

    const selectedVariant = selectedVariantEl
      ? JSON.parse(selectedVariantEl)
      : meta?.product?.variants?.[0];

    let variant = null;

    // Resolved before the first request that can fail. Add to cart now stays
    // off until a print file exists, so every failure path has to say so —
    // otherwise the shopper is left with a dead button and no explanation.
    const regenerateMessage = document.querySelector(
      ".mockup-regenerate-message"
    );
    const messageContent = regenerateMessage.querySelector(".message-content");
    const messageError = regenerateMessage.querySelector(".message-error");
    const messageNotice = regenerateMessage.querySelector(".message-notice");
    clearTimeout(this.noticeTimer);
    regenerateMessage.classList.remove("hidden");
    messageContent.classList.remove("hidden");
    messageError.classList.add("hidden");
    messageNotice?.classList.add("hidden");

    try {
      const response = await this.#fetchWithConfig(
        `${EXT_API_BASE_URL}/products/variants/${selectedVariant.id}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch variant ${selectedVariant.id}`);
      }

      const responseJson = await response.json();
      if (responseJson.error || !responseJson?.data?.id) {
        throw new Error(responseJson.error);
      }

      variant = responseJson.data;
    } catch (err) {
      console.error("[ship-saildate] could not look up the variant.", err);
      messageContent.classList.add("hidden");
      messageError.classList.remove("hidden");
      return;
    }
    let slots;
    try {
      slots = this.#resolveMediaSlots(this.#mockupViews(variant).length);
    } catch (err) {
      console.error("[ship-saildate] product media is not set up correctly.", err);
      messageContent.classList.add("hidden");
      messageError.classList.remove("hidden");
      return;
    }

    const designImageUrl = `${EXT_API_BASE_URL}/itineraries/view/${itineraryId}/${variant.shopify_id}/product-image.jpg`;

    try {
      // Create a promise that rejects after 5 seconds
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(
          () =>
            reject(
              new Error(
                `Design image fetch timed out after ${
                  DESIGN_IMAGE_TIMEOUT_MS / 1000
                } seconds`
              )
            ),
          DESIGN_IMAGE_TIMEOUT_MS
        );
      });

      // Race between the fetch and the timeout
      const designImageResponse = await Promise.race([
        this.#fetchWithConfig(designImageUrl),
        timeoutPromise,
      ]);

      if (!designImageResponse.ok) {
        throw new Error("Failed to fetch design image");
      }

      // Show the bytes just fetched rather than pointing the <img> at the
      // URL again. Setting the URL as src was a second request for the same
      // file, and one whose response, cached without CORS headers, could be
      // handed back to the next scripted fetch. The order still carries the
      // URL; only the preview uses the blob.
      const designBlob = await designImageResponse.blob();
      this.#releaseDesignPreview();
      this.designPreviewUrl = URL.createObjectURL(designBlob);
      slots.design.show(this.designPreviewUrl);

      // Record what fulfilment reads the moment the print file is known to
      // exist. Everything below this point is the shopper's preview, and when
      // it failed these two lines used to fail with it: the order went through
      // carrying neither the print file nor the ship and sail date.
      this.#applyCustomisation(designImageUrl);
    } catch (error) {
      console.error("[ship-saildate] could not build the preview.", error);
      messageContent.classList.add("hidden");
      messageError.classList.remove("hidden");
      return;
    }

    // From here on only the mug mockup is at stake. The design is in the
    // gallery and on the form, so the shopper can already order exactly what
    // they see. Printful's generator sometimes never finishes a task; that is
    // not an issue with the itinerary and it is not worth a dead end, so a
    // failure here keeps the stock mug photo, says so briefly, and moves on.
    try {
      const response = await this.#fetchWithConfig(
        `${EXT_API_BASE_URL}/orders/mockups`,
        {
          method: "POST",
          body: JSON.stringify({
            itineraryId,
            variantId: variant.shopify_id,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch mockup");
      }

      const job = await response.json();
      if (job.error) {
        throw new Error(job.error);
      }

      const jobData = await this.spawnMockupJobPoller(job.data.id);

      if (typeof jobData === "string" && jobData === "error") {
        throw new Error("Failed to generate mockup");
      }

      // Printful returns one mockup per style it was asked for. Each slot
      // takes the mockup for its view, so the views land in order whatever
      // order they come back in; a product with one view takes the first, as
      // it always has, since its style can depend on the itinerary.
      const mockups = jobData?.catalog_variant_mockups?.[0]?.mockups;
      if (Array.isArray(mockups) && mockups.length > 0) {
        const views = this.#mockupViews(variant);
        const used = new Set();
        slots.mockups.forEach((mockupSlot, i) => {
          const view = views[i];
          const mockup =
            views.length === 1
              ? mockups[0]
              : mockups.find(
                  (m) =>
                    !used.has(m) &&
                    (view?.style_id
                      ? m.style_id === view.style_id
                      : m.placement === view?.placement)
                );
          if (!mockup) return;
          used.add(mockup);
          mockupSlot.show(mockup.mockup_url);
        });
      }

      await new Promise((resolve) => setTimeout(resolve, 1000));
      regenerateMessage.classList.add("hidden");
      messageContent.classList.add("hidden");
      messageError.classList.add("hidden");
    } catch (error) {
      console.warn(
        "[ship-saildate] the mug preview did not generate; the design is ready and attached to the order.",
        error
      );
      messageContent.classList.add("hidden");
      messageError.classList.add("hidden");
      if (messageNotice) {
        messageNotice.classList.remove("hidden");
        this.noticeTimer = setTimeout(() => {
          messageNotice.classList.add("hidden");
          regenerateMessage.classList.add("hidden");
        }, MOCKUP_NOTICE_MS);
      } else {
        regenerateMessage.classList.add("hidden");
      }
    }
  }

  async spawnMockupJobPoller(jobId) {
    let attempts = 0;
    const maxAttempts = MOCKUP_POLL_ATTEMPTS;
    const timeout = MOCKUP_POLL_INTERVAL_MS;
    const progressBar = document.querySelector(
      ".mockup-regenerate-message .progress-bar"
    );
    const progressTitle = document.querySelector(
      ".mockup-regenerate-message .regenerate-title"
    );

    const pollMockupJob = async () => {
      const progress = Math.round(
        ((maxAttempts - attempts) / maxAttempts) * 100
      );
      const statusText = REGEN_STATUS_STRINGS[attempts];
      attempts++;
      progressBar.style.width = `${progress}%`;
      if (statusText) {
        progressTitle.innerHTML = statusText;
      }
      console.log(
        `[INFO][${progress}%] Mockup job get status attempt ${attempts} of ${maxAttempts}`
      );

      try {
        const response = await this.#fetchWithConfig(
          `${EXT_API_BASE_URL}/orders/mockups/${jobId}`
        );

        if (!response.ok) {
          throw new Error("Failed to fetch mockup status");
        }

        const result = await response.json();

        if (
          Array.isArray(result.data?.data) &&
          result.data.data.length > 0 &&
          result.data.data[0].status === "completed"
        ) {
          const jobData = result.data.data[0];
          console.log("[INFO] Mockup job completed");
          return jobData;
        }

        if (attempts >= maxAttempts) {
          console.log("Max polling attempts reached");
          throw new Error("Max polling attempts reached");
        }

        await new Promise((resolve) => setTimeout(resolve, timeout));
        return pollMockupJob();
      } catch (error) {
        console.error("Error polling mockup job:", error.message);
        return "error";
      }
    };

    return pollMockupJob();
  }

  /**
   * The Printful mockups that fill the gallery, in slide order, as
   * `{ placement, style_id }`. The API decides them (lib/mockup-views.js in
   * Blairhouse): a can cooler leads with its back, then its front; a mug shows
   * handle on the right, then handle on the left; anything else shows one.
   *
   * An API from before that list existed only ever asked for a second mockup
   * on a two-sided product, so its rule is kept for that case.
   */
  #mockupViews(variant) {
    if (Array.isArray(variant?.mockup_views) && variant.mockup_views.length) {
      return variant.mockup_views;
    }
    return variant?.mockup_style_back_id
      ? [
          { placement: "back" },
          { placement: variant.mockup_placement || "front" },
        ]
      : [{}];
  }

  /** Frees the previous preview's object URL; the browser holds the blob until then. */
  #releaseDesignPreview() {
    if (this.designPreviewUrl) {
      URL.revokeObjectURL(this.designPreviewUrl);
      this.designPreviewUrl = null;
    }
  }

  /**
   * Writes the print file and the ship and sail date onto the form.
   *
   * These two properties are the whole order: without them nobody can tell
   * which voyage was bought or what to print.
   */
  #applyCustomisation(designImageUrl) {
    if (!this.printFileInput || !this.summaryInput) return;
    const shipName =
      this.shipSelect.options[this.shipSelect.selectedIndex].textContent;
    const saildate =
      this.saildateSelect.options[this.saildateSelect.selectedIndex].textContent;
    this.printFileInput.value = designImageUrl;
    this.summaryInput.value = `${shipName} ${saildate.split("-")[0]}`;
    this.updateAddToCartButton();
  }

  /**
   * Drops both properties, before every regeneration and whenever the ship
   * changes.
   *
   * A render that fails partway must not leave the previous voyage's print
   * file attached, which would ship the wrong design rather than none at all.
   */
  #clearCustomisation() {
    if (this.printFileInput) this.printFileInput.value = "";
    if (this.summaryInput) this.summaryInput.value = "";
    this.updateAddToCartButton();
  }

  updateAddToCartButton() {
    const isShipSelected = this.shipSelect.value !== "";
    const isSaildateSelected = this.saildateSelect.value !== "";
    // Order 1606 arrived with no print file and no sail date because this
    // checked the two dropdowns and nothing else, so a render that failed — or
    // simply had not finished — still let the shopper buy an item that could
    // not be made. Selecting is not the same as having a design.
    // Truthiness, not an inequality: if the inputs are missing entirely this
    // has to read as "not customised" and keep the button off, rather than
    // compare undefined against "" and wave the order through.
    const isCustomised = Boolean(
      this.printFileInput?.value && this.summaryInput?.value
    );
    if (this.addToCartButton) {
      this.addToCartButton.disabled =
        !isShipSelected || !isSaildateSelected || !isCustomised;
    }
  }

  onCartVariantChange() {
    const saildateId = this.saildateSelect.value;
    if (saildateId) {
      this.regenerateMockup(saildateId);
    }
  }
}

customElements.define("variant-picker-ship-sailing", VariantPickerShipSailing);
